import SearchIcon from "@mui/icons-material/Search";
import { FormControl, InputAdornment, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { GlobalPropertyInterface, GlobalUserInterface } from "Interface/interface";
import { ReactComponent as ArrowDown } from "assets/ArrowDown_GlobalSearch.svg";
import { setInnCode } from "components/app/App.slice";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, allPlansType, DEFAULT_CORP_INNCODE, minSearchStrLength } from "utils/CommonConstants";
import { getHltInncodes, getUserHltId, useDebouncedEffect } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { ReactComponent as Recent } from "../../../assets/Recent.svg";
import {
  clearInnCodeSearch,
  clearPlanSearch,
  clearSearch,
  fetchGlobalInncodes,
  fetchGlobalPlansAsync,
  fetchGlobalUser,
  fetchInncodeInfo,
  fetchMultiTargeted,
  fetchTemplatePropertyPlansAsync,
  GlobalPlanOption,
  PLAN_TYPES,
  PlanType,
  setCategory,
  setPlanOwner,
  setPropertyPlanList,
  setSearchLoadingCount,
  setTemplatePlanList,
  setUserList,
  TemplatePropertyPlansArg,
  UserPlanOption
} from "./GlobalSearch.slice";
import { selectRecentSearch, selectUserHltId, setRecentSearch } from "./RecentSearch.slice";
import "./index.scss";

function categoryData(isConsultant = false, isCorp = false, isAbovePropertyUser = false) {
  if (isCorp || isConsultant) {
    return ["Property", "Member", "Library Plan"];
  } else if (isAbovePropertyUser) {
    return ["Property", "Member", "Library Plan", "Template Plan", "Property Plan"];
  }
  return ["Library Plan", "Member", "Property Plan"];
}

interface CategoryInterface {
  [key: string]: SearchOption[];
}

/** A row rendered by the dropdown. The concrete shape depends on the selected category. */
type SearchOption = GlobalUserInterface | GlobalPropertyInterface | GlobalPlanOption | UserPlanOption;

/** Category-agnostic view of a row, for the places that read fields without narrowing first. */
type SearchOptionFields = Partial<GlobalUserInterface & GlobalPropertyInterface & GlobalPlanOption & UserPlanOption>;

/** Fields compared when de-duplicating recent searches. */
type DuplicateKey = "innCode" | "email" | "id" | "title";

const GlobalSearch = () => {
  const translate = useTranslate();
  const listInnerRef = useRef();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const innCode = useAppSelector((state) => state.app.innCode);

  const globalUserDetail: GlobalUserInterface[] = useAppSelector((state) => state.globalSearch.globalUserDetail);
  const {
    totalGlobalUser,
    globalUserListLoading,
    category,
    globalPlanList,
    globalInncodeList,
    templatePlanList,
    propertyPlanList,
    planOwnerList,
    searchLoading
  } = useAppSelector((state) => state.globalSearch);
  const { isConsultant, isCorp, isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);
  const loggedInHltId = useAppSelector(selectUserHltId);
  const recentSearches = useAppSelector(selectRecentSearch(loggedInHltId));

  const categories = useMemo(
    () => categoryData(isConsultant, isCorp, isAbovePropertyUser),
    [isConsultant, isCorp, isAbovePropertyUser]
  );

  // Only the first recent entry is labelled in the dropdown
  const firstRecentItem = useMemo(
    () => (isEmpty(searchText) ? recentSearches?.[category]?.[0] : undefined),
    [searchText, category, recentSearches]
  );

  const resultPerPage = 100;
  const isCategoryUser = category === "Member";
  const isCategoryProperty = category === "Property";
  const isCategoryPlan = category === "Plan" || category === "Library Plan";
  const isTemplatePlan = category === "Template Plan";
  const isPropertyPlan = category === "Property Plan";
  const isSearchTextEmpty = searchText === "";

  const associatedCodes = localStorage.getItem("hltInnCodes");

  const isHotelUser = !isCorp && !isConsultant && !isAbovePropertyUser;
  const selectedInncode = isHotelUser ? innCode : isAbovePropertyUser ? associatedCodes : DEFAULT_CORP_INNCODE;
  // Function to handle input change in a Autocomplete
  const handleInputChange = (event: React.SyntheticEvent, value: string, reason: string) => {
    if (!value || value.length < 3) {
      dispatch(clearSearch());
    }
    resetAllData();
    setSearchText(value);
  };

  useEffect(() => {
    return () => {
      resetAllData();
    };
  }, []);

  /* This handleCategoryChange trigger when change the category it reset all the data */
  const handleCategoryChange = (event: SelectChangeEvent) => {
    resetAllData();
    window.stop();
    dispatch(setCategory(event.target.value));
  };

  useEffect(() => {
    if (!isCorp && !isAbovePropertyUser && !isConsultant) {
      dispatch(setCategory("Library Plan"));
    } else {
      dispatch(setCategory("Property"));
    }
  }, [isCorp, isAbovePropertyUser, isConsultant]);

  const findDuplicate = (category: string, globalSearchData: CategoryInterface, item: SearchOptionFields) => {
    const selectedKey = globalSearchData[category];
    const propertyToCheck: DuplicateKey | null = isCategoryProperty
      ? "innCode"
      : isCategoryUser
        ? "email"
        : isCategoryPlan || isPropertyPlan
          ? "id"
          : isTemplatePlan
            ? "title"
            : null;

    if (!propertyToCheck) return false;
    return selectedKey.some((items) => (items as SearchOptionFields)[propertyToCheck] === item[propertyToCheck]);
  };

  // Function to add a new item to recent searches based on category
  const addToRecentSearches = (category: string, item: SearchOption) => {
    if (loggedInHltId) {
      const isDuplicate = findDuplicate(category, recentSearches, item);

      // Create updated recent searches with the new item added to the specified category,
      // ensuring uniqueness and maintaining a maximum of 3 recent items per category
      if (!isDuplicate) {
        const updatedRecentSearches = {
          ...recentSearches,
          [category]: [
            item,
            ...recentSearches[category].filter((searchItem: SearchOption) => searchItem !== item)
          ].slice(0, 3)
        };

        dispatch(setRecentSearch({ hltId: loggedInHltId, recentSearch: updatedRecentSearches }));
      }
    }
  };
  const lastIndexHltId = globalUserDetail[globalUserDetail.length - 1]?.hltId;

  // Function to handle scroll events and load more data if scrolled to the bottom of the list
  const onScroll = async () => {
    if (listInnerRef.current) {
      const mainDiv = document.getElementsByClassName("customAutoComplete")[0];
      const { scrollTop, scrollHeight, clientHeight } = mainDiv;

      // Check if scrolled to the bottom of the list
      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        const isResultComplete = (pageNumber + 1) * resultPerPage < totalGlobalUser;

        if (isResultComplete && !globalUserListLoading) {
          if (isCategoryUser) userLoadMore();
        }
      }
    }
  };

  // Function to reset all data including page number, user list, search text,
  //and dispatching actions to clear search results and loading state
  const resetAllData = useCallback(() => {
    setPageNumber(0);
    setUserList([]);
    setSearchText("");
    dispatch(clearSearch());
    dispatch(clearPlanSearch());
    dispatch(clearInnCodeSearch());
    dispatch(setTemplatePlanList([]));
    dispatch(setPropertyPlanList([]));
    dispatch(setPlanOwner([]));
    dispatch(setSearchLoadingCount(false));
  }, [dispatch]);

  const propertyAndTemplatePlanPayload = (planType: PlanType): TemplatePropertyPlansArg => {
    const payload: TemplatePropertyPlansArg = {
      payload: {
        includeFilters: true,
        includeOwners: true,
        planType: planType,
        searchText: searchText,
        functionalArea: "",
        innCode: planType === PLAN_TYPES.PLAN ? (isHotelUser || isAbovePropertyUser ? getHltInncodes(true) : "") : ""
      },
      page: 0
    };
    if (planType !== PLAN_TYPES.PLAN) payload.payload.searchedHltId = getUserHltId();
    return payload;
  };

  const multitargetApisPayload = () => {
    return {
      innCodes: getHltInncodes(),
      filter: {
        functionalAreaIds: [],
        filterIds: [],
        ids: []
      },
      sort: null,
      pagination: {
        page: 1,
        size: 100
      },
      searchText: searchText,
      planType: [0],
      isSortingDisabled: false
    };
  };

  // Debounced fetch of global search data, 300ms after the search text settles
  useDebouncedEffect(
    async () => {
      if (searchText.length > minSearchStrLength) {
        window.stop();
        if (isCategoryUser) {
          const data = {
            page: 0,
            size: resultPerPage,
            value: searchText,
            includecorpusers: false,
            innCode: isAbovePropertyUser || isHotelUser ? getHltInncodes() : ""
          };
          await dispatch(fetchGlobalUser(data));
        }
        if (isCategoryPlan) {
          if (isAbovePropertyUser || isHotelUser) {
            await dispatch(fetchMultiTargeted(multitargetApisPayload()));
          } else {
            const planData = {
              filter: {
                innCode: selectedInncode,
                functionalAreaIds: [],
                filterIds: []
              },
              pagination: {
                page: 1,
                size: 100
              },
              searchText: searchText,
              planType: [allPlansType],
              sort: {
                property: "relevance",
                direction: "ASC"
              }
            };
            await dispatch(fetchGlobalPlansAsync(planData));
          }
        }
        if (isCategoryProperty) {
          if (isAbovePropertyUser) {
            const innCodePayload = {
              inncodes: getHltInncodes(),
              propNameRequired: true,
              searchText: searchText
            };
            await dispatch(fetchInncodeInfo(innCodePayload));
          } else {
            await dispatch(
              fetchGlobalInncodes({
                searchText,
                isAbovePropertyUser,
                inncode: getHltInncodes(true)
              })
            );
          }
        }

        if (isTemplatePlan) {
          await dispatch(fetchTemplatePropertyPlansAsync(propertyAndTemplatePlanPayload(PLAN_TYPES.TEMPLATE_PLAN)));
        }
        if (isPropertyPlan) {
          await dispatch(fetchTemplatePropertyPlansAsync(propertyAndTemplatePlanPayload(PLAN_TYPES.PLAN)));
        }
      }
    },
    [searchText],
    300
  );

  // Function to load more global user data
  const userLoadMore = async () => {
    setPageNumber(pageNumber + 1);
    const data = {
      page: pageNumber + 1,
      size: resultPerPage,
      value: searchText,
      includecorpusers: false,
      innCode: isHotelUser || isAbovePropertyUser ? getHltInncodes(true) : ""
    };
    await dispatch(fetchGlobalUser(data));
  };

  // Function to render loader or display user data
  const renderLoader = () => {
    return (
      <div className="global-search-loader">
        {globalUserListLoading && <Spinner />}
        <div>
          <Typography color={tokens.Text.General.Color} variant={"label"} showBold>
            <Typography color={tokens.Text.General.Color} variant={"label"} className="pad-r-4">
              {translate("common.showing")}
            </Typography>
            {`${globalUserDetail.length} of ${totalGlobalUser} users`}
          </Typography>
        </div>
      </div>
    );
  };

  // Function to render data based on category
  const renderData = (props: SearchOption) => {
    if (isCategoryUser) {
      return userData(props as GlobalUserInterface);
    }

    if (isCategoryProperty) {
      return propertyData(props as GlobalPropertyInterface);
    }

    if (isCategoryPlan || isTemplatePlan) {
      return planData(props as GlobalPlanOption | UserPlanOption);
    }

    if (isPropertyPlan) {
      return propertyPlanData(props as UserPlanOption);
    }

    return [];
  };

  // Function to render property data
  const propertyData = (props: GlobalPropertyInterface) => {
    return (
      <div className="full-width property-view">
        <div className="info-wrapper">
          <div className="recent-search-wrapper">
            {isSearchTextEmpty && <Recent />}
            <Typography variant="label" color={tokens.Text.General.PrimaryBody} showBold className="ellipse-one">
              {props.innCode}
            </Typography>
          </div>
          <Typography
            variant="body2"
            color={tokens.Text.General.Secondary}
            className={`ellipse-two ${isSearchTextEmpty ? "pad-l-20" : ""}`}
          >
            {props.name}
          </Typography>
        </div>
      </div>
    );
  };

  const planData = (props: GlobalPlanOption | UserPlanOption) => {
    return (
      <div className="full-width plan-view">
        <div className="recent-search-plan">
          {isSearchTextEmpty && <Recent />}
          <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-two">
            {props.name}
          </Typography>
        </div>
      </div>
    );
  };

  const getPlanOwners = (owners: UserPlanOption["owners"]) =>
    planOwnerList.filter((data) => (owners as string[]).includes(data.hltId));

  const propertyPlanData = (props: UserPlanOption) => {
    const planOwners = getPlanOwners(props.owners);
    // Recent-search rows already store resolved owner records, live results resolve them here
    const owners = (isSearchTextEmpty ? props.owners : planOwners) as GlobalUserInterface[];
    const names = owners?.map((item) => item.displayName).join(", ");
    return (
      <div className="full-width property-plan-view">
        <div className="recent-search-plan">
          {isSearchTextEmpty && <Recent />}
          <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-two">
            {props.name}
          </Typography>
        </div>
        <div className={`recent-search-plan ${isSearchTextEmpty ? "ml-20" : ""}`}>
          {isSearchTextEmpty && (
            <div className="visibility-hidden">
              <Recent />
            </div>
          )}
          <Typography variant="body2" color={tokens.Text.General.Secondary}>
            {names}
          </Typography>
        </div>
        <div className={`recent-search-plan ${isSearchTextEmpty ? "ml-20" : ""}`}>
          {isSearchTextEmpty && (
            <div className="visibility-hidden">
              <Recent />
            </div>
          )}
          <Typography variant="body2" color={tokens.Border.General.Secondary} className="ellipse-two">
            {props.innCode}
          </Typography>
        </div>
      </div>
    );
  };

  const getVisibleUserInnCodes = (innCodes: string[] = []) => {
    if (!isHotelUser && !isAbovePropertyUser) return innCodes;
    const associatedInnCodes = getHltInncodes();
    return innCodes.filter((innCode) => associatedInnCodes.includes(innCode));
  };

  // Function to render user data
  const userData = (userInfo: GlobalUserInterface) => {
    const visibleInnCodes = getVisibleUserInnCodes(userInfo?.innCodes);
    const userType =
      userInfo.lobbyUserType.toLowerCase() === ABOVE_PROP_USER_TYPE
        ? translate("common.showing.abovePropertyMember")
        : translate("common.showing.onPropertyMember");
    return (
      <div className="full-width user-view">
        <div className="user-result">
          <div className="user-name">
            {isSearchTextEmpty && <Recent />}
            <Typography variant="body2" showBold color={tokens.Text.General.PrimaryBody} className="ellipse-two">
              {getUserNameWithConditionalCheck(userInfo?.displayName, userInfo?.firstName, userInfo?.lastName)}
            </Typography>
          </div>
          <div className={`${isSearchTextEmpty ? "pad-l-20" : ""}`}>
            <Typography variant="label" color={tokens.Text.General.Secondary}>
              {userType}
            </Typography>
            <div className="lower-content">
              {userInfo.email && (
                <Typography variant="body2" color={tokens.Text.General.Secondary} className="ellipse-one">
                  {userInfo.email}
                </Typography>
              )}
              <div className="user-inncode">
                <Typography variant="body2" color={tokens.Border.General.Secondary} className="ellipse-two">
                  {visibleInnCodes.map((innCOde: string) => {
                    return (
                      <Fragment key={innCOde}>
                        {innCOde}
                        <span className="comma-sepration">,</span>{" "}
                      </Fragment>
                    );
                  })}
                </Typography>
              </div>
            </div>
          </div>
          {lastIndexHltId === userInfo.hltId && renderLoader()}
        </div>
      </div>
    );
  };

  // Function to render option category based on data
  const renderOptionCategory = (data: SearchOption) => {
    const searchStrLower = searchText.toLocaleLowerCase();
    if (isCategoryPlan) {
      return (data as GlobalPlanOption).name;
    }
    if (isTemplatePlan || isPropertyPlan) {
      if ((data as UserPlanOption).title) {
        return "";
      }
    }
    if (isCategoryProperty) {
      const property = data as GlobalPropertyInterface;
      const nameInLowerCase = property?.name?.toLocaleLowerCase();
      const innCodeInLowerCase = property?.innCode?.toLocaleLowerCase();
      if (nameInLowerCase?.includes(searchStrLower)) return property.name;
      if (innCodeInLowerCase?.includes(searchStrLower)) return property.innCode;
    }
    if (isCategoryUser) {
      const user = data as GlobalUserInterface;
      const userName = getUserNameWithConditionalCheck(user?.displayName, user?.firstName, user?.lastName);
      const nameInLowerCase = userName?.toLocaleLowerCase();
      const emailInLowerCase = user?.email?.toLocaleLowerCase();
      if (nameInLowerCase?.includes(searchStrLower)) return userName;
      if (emailInLowerCase?.includes(searchStrLower)) return user.email;
    }
    return " ";
  };

  const addTorecentSearchval = (val: SearchOption): SearchOption => {
    if (isPropertyPlan) {
      const plan = val as UserPlanOption;
      return {
        ...plan,
        owners: getPlanOwners(plan.owners)
      };
    }
    return val;
  };

  const redirectToPage = (value: SearchOption) => {
    const modifiedVal = addTorecentSearchval(value);
    const properties = (value as GlobalPlanOption)?.properties;
    addToRecentSearches(category, modifiedVal);
    if (isCategoryProperty)
      navigate(`/property/${(value as GlobalPropertyInterface)?.innCode}?parentTab=Property+Plans`);
    if (isCategoryUser) {
      const user = value as GlobalUserInterface;
      const userInncode = getVisibleUserInnCodes(user?.innCodes)[0];
      navigate(`/userview/${user?.hltId}?inncode=${userInncode}`);
    }
    if (isCategoryPlan) {
      const planInncode = isCorp || isConsultant ? DEFAULT_CORP_INNCODE : properties[0];
      dispatch(setInnCode(planInncode));
      navigate(`/plandetails/${(value as GlobalPlanOption).id}`);
    }
    if (isTemplatePlan) navigate(`/templateplandetails/${(value as UserPlanOption).id}`);
    if (isPropertyPlan) navigate(`/myplandetails/${(value as UserPlanOption).id}`);
  };

  // Options shown in the dropdown: recent searches while the input is empty, live results otherwise
  const autoCompleteOptions = useMemo(() => {
    if (isEmpty(searchText)) {
      return recentSearches?.[category] ?? [];
    }
    const optionMap: Record<string, SearchOption[]> = {
      Member: globalUserDetail,
      "Library Plan": globalPlanList,
      Property: globalInncodeList,
      "Template Plan": templatePlanList,
      "Property Plan": propertyPlanList
    };
    return optionMap[category] ?? [];
  }, [
    searchText,
    category,
    recentSearches,
    globalUserDetail,
    globalPlanList,
    globalInncodeList,
    templatePlanList,
    propertyPlanList
  ]);

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = () => {
    setIsOpen(false);
  };

  const recentSearch = (info: SearchOptionFields) => {
    const isFirstRecentPlan = isPropertyPlan ? firstRecentItem?.id === info.id : firstRecentItem?.name === info.name;

    if (isCategoryProperty) {
      return (
        <div className="recentSearch-user">
          {firstRecentItem?.innCode === info.innCode && (
            <Typography color={tokens.Border.General.Secondary} variant="label">
              {translate("globalSearch.recentProperty.search")}
            </Typography>
          )}
        </div>
      );
    } else if (isCategoryUser) {
      return (
        <>
          {isSearchTextEmpty && (
            <div className="recentSearch-user">
              {firstRecentItem?.email === info.email && (
                <Typography color={tokens.Border.General.Secondary} variant="label">
                  {translate("globalSearch.recentUser.search")}
                </Typography>
              )}
            </div>
          )}
        </>
      );
    } else {
      return (
        <div className="recentSearch-plan">
          {isFirstRecentPlan && (
            <Typography color={tokens.Border.General.Secondary} variant="label">
              {translate("globalSearch.recentPlan.search")}
            </Typography>
          )}
        </div>
      );
    }
  };

  return (
    <div className="global-search-component">
      <div className="main-container">
        <div className="auto-complete-wrapper">
          <Autocomplete
            popupIcon=""
            className="global-autComplete"
            ref={listInnerRef}
            filterOptions={(option) => option}
            ListboxProps={{
              className: "customAutoComplete",
              onScroll: (event) => {
                onScroll();
              }
            }}
            onChange={(_, newValue) => {
              redirectToPage(newValue as SearchOption);
            }}
            onScroll={onScroll}
            loading={searchLoading}
            loadingText={
              <Typography variant="label" color={tokens.Text.General.Secondary}>
                Loading...
              </Typography>
            }
            freeSolo
            options={autoCompleteOptions}
            getOptionLabel={(user) => {
              return renderOptionCategory(user as SearchOption);
            }}
            onInputChange={handleInputChange}
            inputValue={searchText}
            open={searchText.length > 2 || isOpen}
            renderInput={(params) => (
              <TextField
                onFocus={handleFocus}
                onBlur={handleBlur}
                className="autocomplete-textField"
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment sx={{ paddingLeft: "6px" }} position="start">
                        <SearchIcon />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  }
                }}
              />
            )}
            renderOption={(props, option: SearchOption) => {
              return (
                <>
                  {recentSearch(option)}
                  <li {...props}>{renderData(option)}</li>
                </>
              );
            }}
          />
        </div>
        <div className="category-select-wrapper">
          <FormControl>
            <Select
              IconComponent={ArrowDown}
              value={category}
              onChange={handleCategoryChange}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              className="category-select"
              autoWidth={false}
            >
              {categories.map((category: string) => {
                return (
                  <MenuItem key={category} value={category}>
                    <Typography variant="body2" color={tokens.Text.General.Secondary}>
                      {category}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
